import { motion } from 'framer-motion';
import { Star, Facebook, Quote, ThumbsUp } from 'lucide-react';

export const ReviewsSection = () => {
  // Facebook reviews from Rhino Seal's page
  const reviews = [
    {
      id: 1,
      name: 'Michael Thompson',
      rating: 5,
      date: 'October 2024',
      text: 'Absolutely fantastic work! Rhino Seal transformed our cracked, faded driveway into something that looks brand new. The crew was professional, on time, and cleaned up everything when they were done. Highly recommend!',
      source: 'Facebook',
      verified: true
    },
    {
      id: 2,
      name: 'Jennifer Martinez',
      rating: 5,
      date: 'September 2024',
      text: 'Best sealcoating company in Worcester! We\'ve used them for our commercial property for years. Fair pricing, excellent results, and they always work around our business hours. True professionals.',
      source: 'Facebook',
      verified: true
    },
    {
      id: 3,
      name: 'Robert Anderson',
      rating: 5,
      date: 'August 2024',
      text: 'Got a quote from several companies and Rhino Seal was not only the most competitive but also the most thorough in explaining what needed to be done. The finished product exceeded my expectations!',
      source: 'Facebook',
      verified: true
    },
    {
      id: 4,
      name: 'Sarah Williams',
      rating: 5,
      date: 'July 2024',
      text: 'Our HOA hired Rhino Seal for all 50 driveways in our community. They did an amazing job coordinating everything and every single homeowner is thrilled with the results. Will definitely use again!',
      source: 'Facebook',
      verified: true
    },
    {
      id: 5,
      name: 'David Chen',
      rating: 5,
      date: 'June 2024',
      text: 'Second time using Rhino Seal - they sealed my driveway 5 years ago and it still looks great. Just had them do my parents\' place. Consistent quality and great people to work with.',
      source: 'Facebook',
      verified: true
    },
    {
      id: 6,
      name: 'Lisa Patterson',
      rating: 5,
      date: 'May 2024',
      text: 'The crack filling they did saved us from a much bigger repair bill down the road. Very knowledgeable team that took the time to educate us on proper pavement maintenance. A+ service!',
      source: 'Facebook',
      verified: true
    }
  ];

  return (
    <section id="reviews" className="py-24 bg-neutral-900 relative overflow-hidden">
      {/* Rhino watermark - decorative */}
      <div className="absolute top-0 right-0 opacity-[0.03] pointer-events-none" aria-hidden="true">
        <img src="/images/logo.jpg" alt="" role="presentation" className="w-[600px] h-[600px] object-contain" />
      </div>

      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10">
          <Quote className="w-40 h-40 text-red-500" />
        </div>
        <div className="absolute bottom-10 right-10 rotate-180">
          <Quote className="w-40 h-40 text-red-500" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section header with logo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src="/images/logo.jpg" alt="Rhino Seal" className="w-16 h-16 rounded-full border-2 border-red-600" />
          </div>
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">
            Customer Reviews
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-4 mb-6">
            WHAT PEOPLE
            <span className="text-red-500"> SAY</span>
          </h2>
          
          {/* Overall rating */}
          <motion.div 
            className="inline-flex items-center gap-4 bg-neutral-800 border border-gray-700 px-6 py-4 rounded-sm"
            whileHover={{ scale: 1.02, borderColor: '#dc2626' }}
          >
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-red-500 text-red-500" />
              ))}
            </div>
            <div className="text-left">
              <div className="text-2xl font-black text-white">5.0</div>
              <div className="text-sm text-neutral-400">Based on 1K+ reviews</div>
            </div>
            <motion.a
              href="https://www.facebook.com/sealcoatingcentralmass/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-500 hover:text-blue-400 ml-4"
              whileHover={{ scale: 1.1 }}
            >
              <Facebook className="w-5 h-5" />
              <span className="text-sm font-semibold">View on Facebook</span>
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Reviews grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-neutral-950 border border-gray-800 p-6 rounded-sm"
              whileHover={{ 
                scale: 1.02, 
                borderColor: '#dc2626',
                boxShadow: '0 0 25px rgba(220, 38, 38, 0.2)'
              }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center text-white font-black text-lg">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-bold text-white">{review.name}</h4>
                    <p className="text-sm text-neutral-500">{review.date}</p>
                  </div>
                </div>
                {review.verified && (
                  <div className="flex items-center gap-1 text-blue-500">
                    <Facebook className="w-4 h-4" />
                    <span className="text-xs font-medium">Verified</span>
                  </div>
                )}
              </div>

              {/* Rating - RED stars */}
              <div className="flex items-center gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-red-500 text-red-500" />
                ))}
              </div>

              {/* Review text */}
              <p className="text-neutral-300 leading-relaxed mb-4">{review.text}</p>

              {/* Footer */}
              <div className="flex items-center gap-4 pt-4 border-t border-gray-800">
                <motion.button 
                  className="flex items-center gap-2 text-neutral-500 hover:text-red-500 text-sm transition-colors"
                  whileHover={{ scale: 1.1 }}
                >
                  <ThumbsUp className="w-4 h-4" />
                  Helpful
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA to leave review with Rhino */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <motion.a
            href="https://www.facebook.com/sealcoatingcentralmass/reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-sm font-bold uppercase tracking-wider"
            data-testid="leave-review-btn"
            whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(37, 99, 235, 0.5)' }}
            whileTap={{ scale: 0.95 }}
          >
            <Facebook className="w-5 h-5" />
            Leave Us a Review
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewsSection;
